import { ILegalClusterConfigType, ELegalClusterEnv } from '../entities/IConfiguration';
import { IPlatformConfiguration } from '../entities/IPlatformConfiguration';
import { IPremiumFeatures } from '../entities/IPremiumFeatures';
import * as actionTypes from '../actions/actionTypes/configTypes';
import * as configActions from '../actions/configActions';

const defaultState = (): IConfigReducer => ({
    appConfig: {} as ILegalClusterConfigType,
    appConfigLoaded: false,
    apiUrl: undefined,
    subKey: undefined,
    paymentUrl: undefined,
    subKeyPaymentApi: undefined,
    notificationUrl: undefined,
    messagingApiUrl: undefined,
    signalRUri: undefined,
    organizationsApiUrl: undefined,
    clustersApiUrl: undefined,
    permissionsApiUrl: undefined,
    profilesApiUrl: undefined,
    validationsApiUrl: undefined,
    languageApiUrl: undefined,
    legalDocApiUrl: undefined,
    platformName: undefined,
    isLocalHost: undefined,
    finalRegistration: undefined,
    commonApiUrl: undefined,
    isWopiIntegrationActive: undefined,
    premiumFeatures: null,
    premiumFeaturesLoaded: false,
    platformConfiguration: null,
    platformConfigurationStatus: 'idle',
    platformConfigurationSaving: false
});

export default (state = defaultState(), action): IConfigReducer => {
    switch (action.type) {
        case actionTypes.GET_APPLICATION_CONFIG: {
            const getConfigAction = action as configActions.IGetApplicationConfig; // not needed, this just adds nice code autocompletion
            const splittedApiURI = (getConfigAction?.appConfig?.apiURI || '').split('##');
            const notificationUrl = getConfigAction?.appConfig?.notificationUrl;
            const messagingApiUrl = getConfigAction?.appConfig?.messagingApi;
            const signalRUri = getConfigAction?.appConfig?.signalRUri;
            const organizationsApiUrl = getConfigAction?.appConfig?.organizationsApi;
            const clustersApiUrl = getConfigAction?.appConfig?.clustersApi;
            const permissionsApiUrl = getConfigAction?.appConfig?.permissionsApi;
            const profilesApiUrl = getConfigAction?.appConfig?.profilesApi;
            const commonApiUrl = getConfigAction?.appConfig?.uriCommonApi;
            const validationsApiUrl = getConfigAction?.appConfig?.validationsApi;
            const languageApiUrl = getConfigAction?.appConfig?.languageApi;
            const legalDocApiUrl = getConfigAction?.appConfig?.legalDocApi;
            const platformName = getConfigAction?.appConfig?.platform;
            const isLocalHost = getConfigAction?.appConfig?.isLocalHost;
            const finalRegistration = getConfigAction?.appConfig?.isRegistrationFinalScreenActive;
            const isWopiIntegrationActive = getConfigAction?.appConfig?.isWopiIntegrationActive;

            return {
                ...state,
                appConfig: getConfigAction.appConfig,
                appConfigLoaded: true,
                apiUrl: splittedApiURI[0],
                subKey: splittedApiURI[1],
                notificationUrl,
                messagingApiUrl,
                signalRUri,
                organizationsApiUrl,
                clustersApiUrl,
                permissionsApiUrl,
                profilesApiUrl,
                commonApiUrl,
                validationsApiUrl,
                languageApiUrl,
                legalDocApiUrl,
                platformName,
                isLocalHost,
                finalRegistration,
                isWopiIntegrationActive
            };
        }

        case actionTypes.GET_PREMIUM_CONFIGURATION: {
            const premiumAction = action as configActions.IGetPremiumConfiguration;
            return {
                ...state,
                premiumFeatures: premiumAction.premiumFeatures ?? null,
                premiumFeaturesLoaded: true
            };
        }

        case actionTypes.GET_PLATFORM_CONFIGURATION_REQUEST:
            return {
                ...state,
                platformConfigurationStatus: 'loading'
            };

        case actionTypes.GET_PLATFORM_CONFIGURATION_SUCCESS:
        case actionTypes.UPDATE_PLATFORM_CONFIGURATION_SUCCESS: {
            const platformAction = action as configActions.IGetPlatformConfigurationSuccess;
            return {
                ...state,
                platformConfiguration: platformAction.platformConfiguration,
                platformConfigurationStatus: 'loaded',
                platformConfigurationSaving: false
            };
        }

        case actionTypes.GET_PLATFORM_CONFIGURATION_FAILURE:
            return {
                ...state,
                platformConfigurationStatus: 'error'
            };

        case actionTypes.UPDATE_PLATFORM_CONFIGURATION_REQUEST: {
            const platformAction = action as configActions.IUpdatePlatformConfiguration;
            return {
                ...state,
                platformConfiguration: platformAction.platformConfiguration,
                platformConfigurationSaving: true
            };
        }

        case actionTypes.UPDATE_PLATFORM_CONFIGURATION_FAILURE: {
            const platformAction = action as configActions.IUpdatePlatformConfiguration;
            return {
                ...state,
                platformConfiguration: platformAction.previousPlatformConfiguration ?? state.platformConfiguration,
                platformConfigurationSaving: false
            };
        }

        default:
            return state;
    }
};

export interface IConfigReducer {
    appConfig: ILegalClusterConfigType;
    appConfigLoaded: boolean;
    apiUrl: string;
    subKey: string;
    paymentUrl: string;
    notificationUrl: string;
    messagingApiUrl: string;
    signalRUri: string;
    subKeyPaymentApi: string;
    organizationsApiUrl: string;
    clustersApiUrl: string;
    permissionsApiUrl: string;
    profilesApiUrl: string;
    commonApiUrl: string;
    validationsApiUrl: string;
    languageApiUrl: string;
    legalDocApiUrl: string;
    platformName: ELegalClusterEnv;
    isLocalHost: boolean;
    finalRegistration: boolean;
    isWopiIntegrationActive: boolean;
    premiumFeatures: IPremiumFeatures | null;
    premiumFeaturesLoaded: boolean;
    platformConfiguration: IPlatformConfiguration | null;
    platformConfigurationStatus: TPlatformConfigurationStatus;
    platformConfigurationSaving: boolean;
}

export type TPlatformConfigurationStatus = 'idle' | 'loading' | 'loaded' | 'error';
